import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { generateTimeSlots, TimeOption } from "../../Definitions/RealTimeAvailabilityTypes";
import "./DateTimePicker.css";

interface DateTimePickerProps {
    open: boolean;
    selectedDate: Date | null;
    selectedTime: string | null;
    onConfirm: (date: Date | null, time: string | null) => void;
    onCancel: () => void;
}

const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
/** Always render 6 rows (42 cells) for a consistent calendar grid */
const GRID_SIZE = 42;
const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const YEAR_RANGE_PAST = 0;
const YEAR_RANGE_FUTURE = 5;

/** Inline SVG icons — self-contained, no FA dependency needed on consumer sites */
function ChevronLeft({ className }: { className?: string }) {
    return (
        <svg className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512" width="8" height="14">
            <path fill="currentColor" d="M231.293 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L70.393 256 251.092 74.871c4.686-4.686 4.686-12.284 0-16.971L231.293 38.101c-4.686-4.686-12.284-4.686-16.971 0L4.908 247.515c-4.686 4.686-4.686 12.284 0 16.971L214.322 473.899c4.687 4.686 12.285 4.686 16.971 0z" />
        </svg>
    );
}

function ChevronRight({ className }: { className?: string }) {
    return (
        <svg className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512" width="8" height="14">
            <path fill="currentColor" d="M24.707 38.101L4.908 57.899c-4.686 4.686-4.686 12.284 0 16.971L185.607 256 4.908 437.129c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L41.678 38.101c-4.687-4.686-12.285-4.686-16.971 0z" />
        </svg>
    );
}

function ChevronDown({ className }: { className?: string }) {
    return (
        <svg className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="10" height="10">
            <path fill="currentColor" d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z" />
        </svg>
    );
}

export default function DateTimePicker({ open, selectedDate, selectedTime, onConfirm, onCancel }: DateTimePickerProps) {
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    // Compute default time/date once at mount: next half-hour, or 9 AM tomorrow if after 10 PM
    const defaults = useMemo(() => {
        const now = new Date();
        let h = now.getHours();
        let m = now.getMinutes();
        if (h >= 22) {
            // After 10 PM — default to 9:00 AM next day
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);
            return { time: "09:00", date: tomorrow };
        }
        if (m > 30) { h += 1; m = 0; }
        else if (m > 0) { m = 30; }
        return { time: `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`, date: null };
    }, []);

    const [viewMonth, setViewMonth] = useState(() => {
        const d = selectedDate ?? defaults.date ?? new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [pickedDate, setPickedDate] = useState<Date | null>(selectedDate ?? defaults.date);
    const [pickedTime, setPickedTime] = useState<string | null>(() => {
        if (selectedTime !== null) return selectedTime;
        return defaults.time;
    });
    const [showMonthDropdown, setShowMonthDropdown] = useState(false);
    const [showYearDropdown, setShowYearDropdown] = useState(false);

    const popoverRef = useRef<HTMLDivElement>(null);
    const monthDropdownRef = useRef<HTMLDivElement>(null);
    const yearDropdownRef = useRef<HTMLDivElement>(null);
    const selectedMonthRef = useRef<HTMLDivElement>(null);
    const selectedYearRef = useRef<HTMLDivElement>(null);
    const calendarGridRef = useRef<HTMLDivElement>(null);

    const timeSlots = useMemo(() => generateTimeSlots(), []);

    // Re-sync state from props when sheet opens
    const prevOpenRef = useRef(false);
    useEffect(() => {
        if (open && !prevOpenRef.current) {
            setPickedDate(selectedDate ?? defaults.date);
            setPickedTime(selectedTime ?? defaults.time);
            setShowMonthDropdown(false);
            setShowYearDropdown(false);
            const d = selectedDate ?? defaults.date ?? new Date();
            setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
        }
        prevOpenRef.current = open;
    }, [open, selectedDate, selectedTime, defaults]);

    const isPastMonth = viewMonth.getFullYear() < today.getFullYear() ||
        (viewMonth.getFullYear() === today.getFullYear() && viewMonth.getMonth() <= today.getMonth());

    /** Build a 42-cell grid: trailing days from prev month, current month, leading days from next month */
    const calendarDays = useMemo(() => {
        const year = viewMonth.getFullYear();
        const month = viewMonth.getMonth();
        const firstOfMonth = new Date(year, month, 1);
        const startOffset = firstOfMonth.getDay(); // 0=Sun

        const days: Date[] = [];
        // Previous month trailing days
        const prevMonthLastDate = new Date(year, month, 0).getDate();
        for (let i = startOffset - 1; i >= 0; i--) {
            days.push(new Date(year, month - 1, prevMonthLastDate - i));
        }
        // Current month
        const lastDate = new Date(year, month + 1, 0).getDate();
        for (let d = 1; d <= lastDate; d++) {
            days.push(new Date(year, month, d));
        }
        // Next month leading days
        let nextDay = 1;
        while (days.length < GRID_SIZE) {
            days.push(new Date(year, month + 1, nextDay++));
        }
        return days;
    }, [viewMonth]);

    const years = useMemo(() => {
        const startYear = today.getFullYear() - YEAR_RANGE_PAST;
        const endYear = today.getFullYear() + YEAR_RANGE_FUTURE;
        return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
    }, [today]);

    const goToPrev = () => {
        if (isPastMonth) return;
        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
    };

    const goToNext = () => {
        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
    };

    const isSameDay = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const handleMonthSelect = (monthIndex: number) => {
        setViewMonth(new Date(viewMonth.getFullYear(), monthIndex, 1));
        setShowMonthDropdown(false);
    };

    const handleYearSelect = (year: number) => {
        setViewMonth(new Date(year, viewMonth.getMonth(), 1));
        setShowYearDropdown(false);
    };

    const toggleMonthDropdown = () => {
        setShowMonthDropdown((prev) => !prev);
        setShowYearDropdown(false);
    };

    const toggleYearDropdown = () => {
        setShowYearDropdown((prev) => !prev);
        setShowMonthDropdown(false);
    };

    // Scroll selected month/year into view when dropdown opens
    useEffect(() => {
        if (showMonthDropdown && selectedMonthRef.current) {
            selectedMonthRef.current.scrollIntoView({ block: "nearest" });
        }
    }, [showMonthDropdown]);

    useEffect(() => {
        if (showYearDropdown && selectedYearRef.current) {
            selectedYearRef.current.scrollIntoView({ block: "nearest" });
        }
    }, [showYearDropdown]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (showMonthDropdown && monthDropdownRef.current && !monthDropdownRef.current.contains(target)) {
                setShowMonthDropdown(false);
            }
            if (showYearDropdown && yearDropdownRef.current && !yearDropdownRef.current.contains(target)) {
                setShowYearDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showMonthDropdown, showYearDropdown]);

    // Keyboard navigation for calendar grid
    const handleCalendarKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (!pickedDate && !["Enter", " "].includes(e.key)) return;

        const current = pickedDate ?? today;
        let next: Date | null = null;

        switch (e.key) {
            case "ArrowLeft":
                next = new Date(current.getFullYear(), current.getMonth(), current.getDate() - 1);
                break;
            case "ArrowRight":
                next = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1);
                break;
            case "ArrowUp":
                next = new Date(current.getFullYear(), current.getMonth(), current.getDate() - 7);
                break;
            case "ArrowDown":
                next = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 7);
                break;
            case "Home":
                next = new Date(current.getFullYear(), current.getMonth(), 1);
                break;
            case "End":
                next = new Date(current.getFullYear(), current.getMonth() + 1, 0);
                break;
            case "PageUp":
                e.shiftKey
                    ? (next = new Date(current.getFullYear() - 1, current.getMonth(), current.getDate()))
                    : (next = new Date(current.getFullYear(), current.getMonth() - 1, current.getDate()));
                break;
            case "PageDown":
                e.shiftKey
                    ? (next = new Date(current.getFullYear() + 1, current.getMonth(), current.getDate()))
                    : (next = new Date(current.getFullYear(), current.getMonth() + 1, current.getDate()));
                break;
            default:
                return;
        }

        if (next) {
            e.preventDefault();
            next.setHours(0, 0, 0, 0);
            if (next >= today) {
                setPickedDate(next);
                // Update view month if navigated outside current view
                if (next.getMonth() !== viewMonth.getMonth() || next.getFullYear() !== viewMonth.getFullYear()) {
                    setViewMonth(new Date(next.getFullYear(), next.getMonth(), 1));
                }
            }
        }
    }, [pickedDate, today, viewMonth]);

    // Trap focus inside popover for accessibility
    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onCancel();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open, onCancel]);

    // Track mobile breakpoint so we can portal only on mobile (bottom sheet).
    // On desktop the popover uses position:absolute relative to its trigger parent.
    const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
    useEffect(() => {
        const mql = window.matchMedia("(max-width: 767px)");
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, []);

    // Lock background scroll on mobile — iOS Safari ignores overflow:hidden on body,
    // so we pin the body with position:fixed and restore scroll position on close.
    useEffect(() => {
        if (!open || !isMobile) return;
        const scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            document.body.style.overflow = "";
            window.scrollTo(0, scrollY);
        };
    }, [open, isMobile]);

    const monthLabel = `${MONTH_NAMES[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`;

    const content = (
        <>
            <div className={"dtp-overlay" + (open ? " open" : "")} onClick={onCancel} aria-hidden="true" />
            <div
                className={"dtp-popover" + (open ? " open" : "")}
                ref={popoverRef}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal={open}
                aria-hidden={!open}
                aria-label="Date and time picker"
            >
                <div className="dtp-sheet-header">
                    <h3 className="dtp-sheet-title">Select Date &amp; Time</h3>
                    <button className="dtp-sheet-close" onClick={onCancel} aria-label="Close">&times;</button>
                </div>
                <div className="dtp-body">
                    <div className="dtp-calendar">
                        <CalendarHeader
                            month={viewMonth}
                            years={years}
                            canGoPrev={!isPastMonth}
                            showMonthDropdown={showMonthDropdown}
                            showYearDropdown={showYearDropdown}
                            monthDropdownRef={monthDropdownRef}
                            yearDropdownRef={yearDropdownRef}
                            selectedMonthRef={selectedMonthRef}
                            selectedYearRef={selectedYearRef}
                            onPrev={goToPrev}
                            onNext={goToNext}
                            onToggleMonth={toggleMonthDropdown}
                            onToggleYear={toggleYearDropdown}
                            onSelectMonth={handleMonthSelect}
                            onSelectYear={handleYearSelect}
                        />

                        <div className="dtp-weekdays" role="row">
                            {WEEKDAYS.map((d) => (
                                <div key={d} role="columnheader" aria-label={d}>
                                    {d}
                                </div>
                            ))}
                        </div>

                        <div
                            className="dtp-days"
                            role="grid"
                            ref={calendarGridRef}
                            aria-label={monthLabel}
                            onKeyDown={handleCalendarKeyDown}
                        >
                            {calendarDays.map((date, i) => {
                                const isOutsideMonth = date.getMonth() !== viewMonth.getMonth();
                                const isPast = date < today;
                                const isSelected = pickedDate !== null && isSameDay(date, pickedDate);
                                const isToday = isSameDay(date, today);

                                let cls = "dtp-day";
                                if (isOutsideMonth) cls += " dtp-day-outside";
                                if (isPast) cls += " dtp-day-disabled";
                                if (isSelected) cls += " dtp-day-selected";
                                if (isToday && !isSelected) cls += " dtp-day-today";

                                const dateLabel = date.toLocaleDateString("en-US", {
                                    weekday: "long", month: "long", day: "numeric", year: "numeric"
                                });

                                return (
                                    <button
                                        key={i}
                                        className={cls}
                                        disabled={isPast}
                                        tabIndex={isSelected || (pickedDate === null && isToday) ? 0 : -1}
                                        role="gridcell"
                                        aria-selected={isSelected}
                                        aria-disabled={isPast}
                                        aria-label={dateLabel}
                                        aria-current={isToday ? "date" : undefined}
                                        onClick={() => {
                                            setPickedDate(date);
                                            if (isOutsideMonth) {
                                                setViewMonth(new Date(date.getFullYear(), date.getMonth(), 1));
                                            }
                                        }}
                                    >
                                        {date.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <TimeColumn
                        timeSlots={timeSlots}
                        selected={pickedTime}
                        onSelect={(val) => {
                            setPickedTime(val);
                            // Selecting "Anytime" resets date to null
                            if (val === null) setPickedDate(null);
                        }}
                        isToday={pickedDate === null || isSameDay(pickedDate, today)}
                        open={open}
                    />
                </div>

                <div className="dtp-footer">
                    <button className="v-button secondary" onClick={onCancel}>Cancel</button>
                    <button className="v-button primary" onClick={() => onConfirm(pickedTime === null ? null : pickedDate, pickedTime)}>OK</button>
                </div>
            </div>
        </>
    );

    // Portal on mobile to escape container-type containment; render inline on desktop
    // where position:absolute needs the trigger parent as containing block.
    return isMobile ? createPortal(content, document.body) : content;
}

interface CalendarHeaderProps {
    month: Date;
    years: number[];
    canGoPrev: boolean;
    showMonthDropdown: boolean;
    showYearDropdown: boolean;
    monthDropdownRef: React.RefObject<HTMLDivElement>;
    yearDropdownRef: React.RefObject<HTMLDivElement>;
    selectedMonthRef: React.RefObject<HTMLDivElement>;
    selectedYearRef: React.RefObject<HTMLDivElement>;
    onPrev: () => void;
    onNext: () => void;
    onToggleMonth: () => void;
    onToggleYear: () => void;
    onSelectMonth: (index: number) => void;
    onSelectYear: (year: number) => void;
}

function CalendarHeader({
    month, years, canGoPrev,
    showMonthDropdown, showYearDropdown,
    monthDropdownRef, yearDropdownRef,
    selectedMonthRef, selectedYearRef,
    onPrev, onNext,
    onToggleMonth, onToggleYear,
    onSelectMonth, onSelectYear,
}: CalendarHeaderProps) {
    const currentMonthIndex = month.getMonth();
    const currentYear = month.getFullYear();

    const handleMonthKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelectMonth(index);
        }
    };

    const handleYearKeyDown = (e: React.KeyboardEvent, year: number) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelectYear(year);
        }
    };

    return (
        <div className="dtp-cal-header">
            <button
                className="dtp-cal-nav"
                disabled={!canGoPrev}
                onClick={onPrev}
                aria-label="Previous month"
            >
                <ChevronLeft />
            </button>

            <div className="dtp-header-dropdowns">
                {/* Month dropdown */}
                <div
                    className={`dtp-dropdown${showMonthDropdown ? " dtp-dropdown-open" : ""}`}  
                    ref={monthDropdownRef}
                >
                    <button
                        className="dtp-dropdown-trigger"
                        onClick={onToggleMonth}
                        aria-haspopup="listbox"
                        aria-expanded={showMonthDropdown}
                        aria-label={`Select month, current: ${MONTH_NAMES[currentMonthIndex]}`}
                    >
                        {MONTH_NAMES[currentMonthIndex]}
                        <ChevronDown className="dtp-dropdown-arrow" />
                    </button>
                    {showMonthDropdown && (
                        <div className="dtp-dropdown-options" role="listbox" aria-label="Month selection">
                            {MONTH_NAMES.map((name, index) => {
                                const isSelected = index === currentMonthIndex;
                                return (
                                    <div
                                        key={name}
                                        className={`dtp-dropdown-option${isSelected ? " dtp-dropdown-option-selected" : ""}`}
                                        ref={isSelected ? selectedMonthRef : null}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        onClick={() => onSelectMonth(index)}
                                        onKeyDown={(e) => handleMonthKeyDown(e, index)}
                                    >
                                        {name}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Year dropdown */}
                <div
                    className={`dtp-dropdown${showYearDropdown ? " dtp-dropdown-open" : ""}`}
                    ref={yearDropdownRef}
                >
                    <button
                        className="dtp-dropdown-trigger"
                        onClick={onToggleYear}
                        aria-haspopup="listbox"
                        aria-expanded={showYearDropdown}
                        aria-label={`Select year, current: ${currentYear}`}
                    >
                        {currentYear}
                        <ChevronDown className="dtp-dropdown-arrow" />
                    </button>
                    {showYearDropdown && (
                        <div className="dtp-dropdown-options" role="listbox" aria-label="Year selection">
                            {years.map((year) => {
                                const isSelected = year === currentYear;
                                return (
                                    <div
                                        key={year}
                                        className={`dtp-dropdown-option${isSelected ? " dtp-dropdown-option-selected" : ""}`}
                                        ref={isSelected ? selectedYearRef : null}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        onClick={() => onSelectYear(year)}
                                        onKeyDown={(e) => handleYearKeyDown(e, year)}
                                    >
                                        {year}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <button
                className="dtp-cal-nav"
                onClick={onNext}
                aria-label="Next month"
            >
                <ChevronRight />
            </button>
        </div>
    );
}

function TimeColumn({ timeSlots, selected, onSelect, isToday, open }: {
    timeSlots: TimeOption[]; selected: string | null; onSelect: (val: string | null) => void; isToday: boolean; open: boolean;
}) {
    const listRef = useRef<HTMLDivElement>(null);
    const selectedTimeRef = useRef<HTMLButtonElement>(null);
    const nextHalfHourRef = useRef<HTMLButtonElement>(null);

    /** Next upcoming half-hour value string (e.g. "14:30").
     *  After 10 PM defaults to 9:00 AM (next day handled by parent). */
    const nextHalfHour = useMemo(() => {
        const now = new Date();
        const h = now.getHours();
        if (h >= 22) return "09:00";
        let nh = h;
        let m = now.getMinutes();
        if (m > 30) { nh += 1; m = 0; }
        else if (m > 0) { m = 30; }
        return `${nh.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }, []);

    // Time slots only (no Anytime) in natural 12:00 AM → 11:30 PM order
    const timedSlots = useMemo(() => timeSlots.filter(s => s.value !== ""), [timeSlots]);

    const isAnytimeSelected = selected === null;

    // Scroll so the next half-hour (or selected time) is visible near the top
    useEffect(() => {
        if (!open) return;
        const list = listRef.current;
        const target = selectedTimeRef.current ?? nextHalfHourRef.current;
        if (!list || !target) return;
        list.scrollTop = target.offsetTop - list.offsetTop;
    }, [open]);

    const handleTimeKeyDown = (e: React.KeyboardEvent, slot: TimeOption) => {
        const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>("button");
        if (!buttons) return;

        const currentIndex = Array.from(buttons).findIndex((btn) => btn === e.currentTarget);
        let nextIndex = -1;

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                nextIndex = Math.min(currentIndex + 1, buttons.length - 1);
                break;
            case "ArrowUp":
                e.preventDefault();
                nextIndex = Math.max(currentIndex - 1, 0);
                break;
            case "Home":
                e.preventDefault();
                nextIndex = 0;
                break;
            case "End":
                e.preventDefault();
                nextIndex = buttons.length - 1;
                break;
            case "Enter":
            case " ":
                e.preventDefault();
                onSelect(slot.value === "" ? null : slot.value);
                return;
            default:
                return;
        }

        if (nextIndex >= 0 && buttons[nextIndex]) {
            buttons[nextIndex].focus();
        }
    };

    return (
        <div className="dtp-time-col" role="group" aria-label="Time selection">
            <div className="dtp-time-header">
                <button
                    className={"dtp-time-item dtp-time-anytime" + (isAnytimeSelected ? " dtp-time-selected" : "")}
                    role="option"
                    aria-selected={isAnytimeSelected}
                    tabIndex={isAnytimeSelected ? 0 : -1}
                    onClick={() => onSelect(null)}
                >
                    Anytime
                </button>
            </div>

            <div
                className="dtp-time-list"
                ref={listRef}
                role="listbox"
                aria-label="Time selection"
                tabIndex={0}
            >
                {timedSlots.map((slot) => {
                    const isSelected = selected === slot.value;
                    const isPast = isToday && slot.value < nextHalfHour;
                    const isNextHalfHour = slot.value === nextHalfHour;
                    return (
                        <button
                            key={slot.value}
                            className={
                                "dtp-time-item"
                                + (isSelected ? " dtp-time-selected" : "")
                                + (isPast ? " dtp-time-past" : "")
                            }
                            ref={isSelected ? selectedTimeRef : isNextHalfHour ? nextHalfHourRef : null}
                            role="option"
                            aria-selected={isSelected}
                            tabIndex={isSelected ? 0 : -1}
                            onClick={() => onSelect(slot.value)}
                            onKeyDown={(e) => handleTimeKeyDown(e, slot)}
                        >
                            {slot.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
